import { useState, useMemo } from "react";
import {
  Briefcase,
  CheckCircle2,
  Globe,
  Clock,
  Download,
} from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  useJobPostingStats,
  useCrawlRuns,
} from "@/features/job-postings/queries/useJobPostings";
import { CrawlRunsTable } from "./CrawlRunsTable";
import { ImportJobPostingsDialog } from "./ImportJobPostingsDialog";

export function JobPostingsPanel() {
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { data: crawlRuns, isLoading: crawlRunsLoading } = useCrawlRuns(50);

  const hasRunning = crawlRuns?.some(
    (r) => r.status.toLowerCase() === "running"
  );

  const { data: postingStats } = useJobPostingStats({
    refetchInterval: hasRunning ? 5_000 : false,
  });

  const runs = crawlRuns ?? [];

  const stats = useMemo(() => {
    const totalPostings = postingStats?.totalCount ?? 0;
    const activePostings = postingStats?.activeCount ?? 0;

    const sources = new Set(runs.map((r) => r.sourceName)).size;

    const lastCompleted = runs.find(
      (r) => r.status.toLowerCase() === "completed"
    );
    const lastCrawl = lastCompleted
      ? new Date(lastCompleted.startedAt).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "Never";

    return { totalPostings, activePostings, sources, lastCrawl };
  }, [postingStats, runs]);

  const cards = useMemo(
    () => [
      {
        id: "total",
        title: "Total Postings",
        description: `${stats.totalPostings} job postings stored`,
        icon: Briefcase,
        value: stats.totalPostings,
      },
      {
        id: "active",
        title: "Active",
        description: `${stats.activePostings} currently active`,
        icon: CheckCircle2,
        value: stats.activePostings,
        color: "text-emerald-600",
      },
      {
        id: "sources",
        title: "Sources",
        description: `${stats.sources} crawl source(s) configured`,
        icon: Globe,
        value: stats.sources,
      },
      {
        id: "last-crawl",
        title: "Last Crawl",
        description: stats.lastCrawl,
        icon: Clock,
        value: null,
      },
    ],
    [stats]
  );

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between">
        <p className="mt-1 text-sm text-ink-500">
          Monitor crawl activity and job posting statistics
        </p>
        <Button
          variant="default"
          size="sm"
          onClick={() => setImportDialogOpen(true)}
        >
          <Download className="h-4 w-4 mr-1.5" />
          Import Job Postings
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <article
              key={card.id}
              className="rounded-xl border border-grid-200 bg-white p-4 shadow-sm shadow-ink-900/5"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon
                      className={`h-4 w-4 shrink-0 ${card.color || "text-ink-600"}`}
                    />
                    <h3 className="text-sm font-semibold text-ink-900 truncate">
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-xs text-ink-500 line-clamp-2">
                    {card.description}
                  </p>
                </div>
                {card.value !== null && (
                  <div className="text-2xl font-bold text-ink-900 ml-2 shrink-0">
                    {card.value}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* Crawl runs table */}
      <div>
        <h3 className="text-sm font-semibold text-ink-900 mb-3">
          Crawl History
        </h3>
        <div className="rounded-xl border border-grid-200 bg-white shadow-sm overflow-hidden">
          <CrawlRunsTable runs={runs} isLoading={crawlRunsLoading} />
        </div>
      </div>

      <ImportJobPostingsDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />
    </div>
  );
}
